import { Dayjs } from "dayjs";
import { countryList } from "../countryCodes";
import { isValidObjectId } from "../find_unique_ids_in_list";
import {
	areFindingOutcomesShown,
	getFindingStatusCounts,
	isReportFinished,
} from "./findingFilters";
import { getReportAuthorEmail } from "./reportAuthor";
import { ReportingHubFilterSelection } from "./reportingHubFilters";
import { getReportStatusKey, ReportStatusKey } from "./reportStatusOptions";

// A report stream's list (pages/reporting/*Stream): what is searched,
// filtered, sorted and paged, kept in the page's address so that Back from a
// report brings the list back and a copied address opens the same list.

export type StreamSort = { key: string; descending: boolean };

export type ReportStreamState = {
	search: string;
	// The status figures turned on.
	statuses: ReportStatusKey[];
	// Each filter's chosen values, by the filter's key.
	filters: Record<string, string[]>;
	sort?: StreamSort;
	page: number;
	pageSize: number;
};

export type ReportStreamParamOptions = {
	// The stream's status figures, in their order, and those on by default.
	statuses: readonly ReportStatusKey[];
	defaultStatuses: readonly ReportStatusKey[];
	filterKeys: readonly string[];
	sortKeys: readonly string[];
};

// The report fields these read.
export type StreamReport = {
	status?: string | null;
	open_status?: string | null;
	is_rejected?: boolean | null;
};

export const STREAM_PAGE_SIZES = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const SEARCH_PARAM = "q";
const STATUS_PARAM = "status";
const SORT_PARAM = "sort";
const PAGE_PARAM = "page";
const SIZE_PARAM = "size";

// A list param's values, once each. They hold no commas: tenant keys, ids
// and type keys.
const splitList = (value: string | null) =>
	value === null
		? []
		: [...new Set(value.split(",").filter((part) => part !== ""))];

// Whether a filter value can be kept in the address: text, not empty, and
// without the comma that splitList splits on. Other values, such as a
// legacy report's stored text with a comma, are never offered.
export const isStreamParamValue = (value: unknown): value is string =>
	typeof value === "string" && value !== "" && !value.includes(",");

const sameValues = (a: readonly string[], b: readonly string[]) =>
	a.length === b.length && a.every((value) => b.includes(value));

// "date" sorts up and "-date" down.
const formatSortParam = (sort?: StreamSort) => {
	if (!sort) return "";
	return sort.descending ? `-${sort.key}` : sort.key;
};

export function parseReportStreamParams(
	params: URLSearchParams,
	options: ReportStreamParamOptions
): ReportStreamState {
	const statusParam = params.get(STATUS_PARAM);
	const statuses =
		statusParam === null
			? [...options.defaultStatuses]
			: options.statuses.filter((key) => splitList(statusParam).includes(key));

	const sortParam = params.get(SORT_PARAM) ?? "";
	const descending = sortParam.startsWith("-");
	const sortKey = descending ? sortParam.slice(1) : sortParam;

	const pageParam = params.get(PAGE_PARAM) ?? "";
	const page = /^\d+$/.test(pageParam) ? Number(pageParam) : 1;
	const pageSize = Number(params.get(SIZE_PARAM));

	return {
		search: params.get(SEARCH_PARAM) ?? "",
		statuses,
		filters: Object.fromEntries(
			options.filterKeys.map((key) => [key, splitList(params.get(key))])
		),
		sort: options.sortKeys.includes(sortKey)
			? { key: sortKey, descending }
			: undefined,
		page: Math.max(1, page),
		pageSize: STREAM_PAGE_SIZES.includes(pageSize)
			? pageSize
			: DEFAULT_PAGE_SIZE,
	};
}

// The params with the stream's state written in: defaults are left out,
// every figure off is an empty status, and any other param, such as
// tenant_key, is kept.
export function writeReportStreamParams(
	params: URLSearchParams,
	state: ReportStreamState,
	options: ReportStreamParamOptions
): URLSearchParams {
	const next = new URLSearchParams(params);
	const set = (key: string, value: string) => {
		if (value === "") next.delete(key);
		else next.set(key, value);
	};

	set(SEARCH_PARAM, state.search);
	if (sameValues(state.statuses, options.defaultStatuses)) {
		next.delete(STATUS_PARAM);
	} else {
		next.set(
			STATUS_PARAM,
			options.statuses.filter((key) => state.statuses.includes(key)).join(",")
		);
	}
	for (const key of options.filterKeys) {
		set(key, (state.filters[key] ?? []).join(","));
	}
	set(SORT_PARAM, formatSortParam(state.sort));
	set(PAGE_PARAM, state.page > 1 ? String(state.page) : "");
	set(
		SIZE_PARAM,
		state.pageSize === DEFAULT_PAGE_SIZE ? "" : String(state.pageSize)
	);
	return next;
}

// Whether the search, the figures or the filters differ from the defaults:
// what Reset filters puts back. The sort and the page size aren't filters.
export function hasActiveStreamFilters(
	state: ReportStreamState,
	options: ReportStreamParamOptions
) {
	return (
		state.search.trim() !== "" ||
		!sameValues(state.statuses, options.defaultStatuses) ||
		Object.values(state.filters).some((values) => values.length > 0)
	);
}

// The operator, project, well and operation filters to write after one of
// them changed. With one operator or none, the Operator filter is hidden, so
// choosing a project, well or operation leaves the operator as it was: the
// cascade would choose that operator too, which narrows nothing and would
// bring the hidden filter up.
export function getCascadeFilters(
	next: ReportingHubFilterSelection,
	isOperatorChange: boolean,
	operatorCount: number,
	operators: string[]
): Record<string, string[]> {
	return {
		operator:
			isOperatorChange || operatorCount > 1 ? next.operators : operators,
		project: next.projects,
		well: next.wells,
		operation: next.operations,
	};
}

// Whether every word of the query is in one of the texts, ignoring case.
// Only text counts: nothing checks what else could be stored.
export function matchesStreamSearch(texts: readonly unknown[], query: string) {
	const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
	if (words.length === 0) return true;
	const haystack = texts
		.filter((text): text is string => typeof text === "string")
		.map((text) => text.toLowerCase());
	return words.every((word) => haystack.some((text) => text.includes(word)));
}

// The options a filter offers: the distinct values in the rows that the
// address can keep, by label.
export function getStreamFilterOptions<T>(
	rows: readonly T[],
	getValue: (row: T) => unknown,
	getLabel: (value: string) => string
) {
	const values = new Set<string>();
	for (const row of rows) {
		const value = getValue(row);
		if (isStreamParamValue(value)) values.add(value);
	}
	return [...values]
		.map((value) => ({ label: getLabel(value), value }))
		.sort((a, b) => a.label.localeCompare(b.label));
}

// A report number's digits as one number, as the streams have always sorted
// them: "EE-AUD-48" is 48.
export function getReportNumberValue(reportNumber: unknown) {
	if (typeof reportNumber !== "string") return undefined;
	const digits = reportNumber.replace(/\D/g, "");
	return digits === "" ? undefined : Number(digits);
}

// A date as a timestamp; none for a missing or invalid one.
export function getDateValue(value: unknown) {
	if (typeof value !== "string" && !(value instanceof Date)) return undefined;
	const time = new Date(value).getTime();
	return Number.isNaN(time) ? undefined : time;
}

export type StreamSortValues<T> = Record<
	string,
	(row: T) => number | undefined
>;

// The rows in a sort's order: by the sort's value, with rows that have none
// last either way and equal values in the server's order. Without a sort,
// the server's order.
export function sortStreamRows<T>(
	rows: readonly T[],
	sort: StreamSort | undefined,
	sortValues: StreamSortValues<T>
): T[] {
	const getValue = sort ? sortValues[sort.key] : undefined;
	if (!sort || !getValue) return [...rows];
	const direction = sort.descending ? -1 : 1;
	return rows
		.map((row, index) => ({ row, index, value: getValue(row) }))
		.sort((a, b) => {
			if (a.value === undefined || b.value === undefined) {
				if (a.value === b.value) return a.index - b.index;
				return a.value === undefined ? 1 : -1;
			}
			return (a.value - b.value) * direction || a.index - b.index;
		})
		.map(({ row }) => row);
}

// How many reports are at each status. Reports at none aren't counted.
export function countByStatus(rows: readonly StreamReport[]) {
	const counts: Partial<Record<ReportStatusKey, number>> = {};
	for (const row of rows) {
		const key = getReportStatusKey(row.status, row.open_status);
		if (key) counts[key] = (counts[key] ?? 0) + 1;
	}
	return counts;
}

// The reports at the statuses turned on. With none on, none.
export const filterByStatuses = <T extends StreamReport>(
	rows: readonly T[],
	statuses: readonly ReportStatusKey[]
) =>
	rows.filter((row) => {
		const key = getReportStatusKey(row.status, row.open_status);
		return key !== undefined && statuses.includes(key);
	});

// One page of the rows: the page, kept within the pages there are, and the
// 1-based range it shows ("Showing 1–20 of 32"), or 0 to 0 with no rows.
export function getPageSlice<T>(
	rows: readonly T[],
	page: number,
	pageSize: number
) {
	const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
	const current = Math.min(Math.max(1, page), pageCount);
	const start = (current - 1) * pageSize;
	const pageRows = rows.slice(start, start + pageSize);
	return {
		rows: pageRows,
		page: current,
		from: pageRows.length > 0 ? start + 1 : 0,
		to: start + pageRows.length,
	};
}

export type StreamItemsSummary =
	| { kind: "count"; total: number }
	| { kind: "progress"; total: number; completed: number; overdue: number };

// How a stream sums up a report's findings or actions, by its page's rule
// (ReportItemsPanel): only how many there are until the report goes out for
// action; then how many are completed, and how many are overdue while their
// deadlines still count.
export function getItemsSummary(
	items: unknown,
	report: StreamReport,
	today: Dayjs
): StreamItemsSummary {
	const list = Array.isArray(items) ? items : [];
	if (
		!areFindingOutcomesShown(
			report.status,
			report.open_status,
			report.is_rejected
		)
	) {
		return { kind: "count", total: list.length };
	}
	const counts = getFindingStatusCounts(
		list,
		today,
		!isReportFinished(report.status, report.open_status)
	);
	return {
		kind: "progress",
		total: counts.total,
		completed: counts.completed,
		overdue: counts.overdue,
	};
}

// Only text counts: nothing checks what else could be stored.
export const streamText = (value: unknown) =>
	typeof value === "string" ? value.trim() : "";

// What looks like an email, lower-cased, once each, sorted.
export const toStreamEmails = (values: unknown[]) =>
	[
		...new Set(
			values
				.map((value) => streamText(value).toLowerCase())
				.filter((value) => value.includes("@"))
		),
	].sort((a, b) => a.localeCompare(b));

// The name looked up for a value, if it is an email: only emails are looked
// up, so other text never reads an object's own keys, such as "constructor".
export function getStreamNameFor(
	emailToName: Record<string, string>,
	value: unknown
): string | undefined {
	const email = streamText(value).toLowerCase();
	return email.includes("@") ? emailToName[email] : undefined;
}

type AuthoredStreamReport = {
	primary_author?: string | null;
	author?: string | null;
};

// The authors to look up by name.
export const collectAuthorEmails = (rows: readonly AuthoredStreamReport[]) =>
	toStreamEmails(rows.map((row) => getReportAuthorEmail(row)));

// The author as a stream shows them: a known user's name, otherwise the
// stored email.
export function getReportAuthorName(
	report: AuthoredStreamReport,
	emailToName: Record<string, string>
) {
	const stored = streamText(getReportAuthorEmail(report));
	if (stored === "") return "—";
	return getStreamNameFor(emailToName, stored) || stored; // NOSONAR: an empty name falls back to the email
}

// The records a stream has looked up, by id: the reports' projects, wells,
// operations and phases.
export type StreamPlaces = {
	projectMap: Record<string, any>;
	wellMap: Record<string, any>;
	operationMap: Record<string, any>;
	phaseMap: Record<string, any>;
};

// The fields of a report that name where it belongs.
export type StreamPlaceReport = {
	project?: unknown;
	well?: unknown;
	operation?: unknown;
	phase?: unknown;
};

// The record an id names. Only ids are looked up, so other text never reads
// an object's own keys.
const getRecord = (map: Record<string, any>, id: unknown) =>
	isValidObjectId(id) ? map[id as string] : undefined;

// A report's field as the id of a record, such as an MoC's category: only
// ObjectId text is one.
export const getStreamRecordId = (value: unknown) =>
	isValidObjectId(value) ? (value as string) : undefined;

// The name of the record an id names, such as an MoC's category or an NCR's
// department, from the record's `field`; none for an empty name.
export function getStreamRecordName(
	map: Record<string, any>,
	id: unknown,
	field = "name"
) {
	const name = streamText(getRecord(map, id)?.[field]);
	return name === "" ? undefined : name;
}

// How a filter names a record: by its name, or as unknown while the records
// load, or for one deleted or in a tenant the user can't read.
export const getStreamRecordLabel = (
	map: Record<string, any>,
	id: unknown,
	unknownLabel: string,
	field = "name"
) => getStreamRecordName(map, id, field) ?? unknownLabel;

// The code of the project a report's id names, which a stream's search
// finds.
export function getReportProjectCode(
	report: StreamPlaceReport,
	places: StreamPlaces
) {
	const code = streamText(
		getRecord(places.projectMap, report.project)?.project_code
	);
	return code === "" ? undefined : code;
}

// A country's name, from the alpha-3 code a project stores.
const getCountryName = (code: unknown) =>
	streamText(
		countryList.find((country: any) => country["alpha-3"] === code)?.name
	);

// Where a report belongs, as the line under its title names it: its rig and
// country, its well and its phase, each only when the report's ids name one.
// It doesn't depend on the report's "Is this related to a project?" switch,
// which reports from before the switch don't have.
export function getReportPlaceParts(
	report: StreamPlaceReport,
	places: StreamPlaces
) {
	const rig = streamText(getRecord(places.operationMap, report.operation)?.rig);
	const country = getCountryName(
		getRecord(places.projectMap, report.project)?.country
	);
	return [
		[rig, country].filter((part) => part !== "").join(", "),
		streamText(getRecord(places.wellMap, report.well)?.well_name),
		streamText(getRecord(places.phaseMap, report.phase)?.phase_name),
	].filter((part) => part !== "");
}
